import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';

const Icon = ({ path }: { path: React.ReactNode }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="w-[18px] h-[18px] flex-shrink-0" aria-hidden="true">
    {path}
  </svg>
);

interface NavItem { path: string; label: string; icon: React.ReactNode; badge?: number; soon?: boolean; }
interface NavSection { label: string; items: NavItem[]; }

const NavLink: React.FC<{ item: NavItem; active: boolean; onClick: () => void }> = ({ item, active, onClick }) => (
  <button
    type="button"
    onClick={item.soon ? undefined : onClick}
    className={`nav-item w-full text-left ${active ? 'active' : ''} ${item.soon ? 'cursor-default' : ''}`}
    style={{ opacity: item.soon ? 0.4 : 1 }}
    title={item.soon ? 'Coming soon' : undefined}
  >
    <span className="flex-shrink-0">{item.icon}</span>
    <span className="flex-1 truncate text-sm">{item.label}</span>
    {item.badge !== undefined && item.badge > 0 && (
      <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center"
        style={{ background: 'var(--crimson)', color: '#fff' }}>
        {item.badge > 99 ? '99+' : item.badge}
      </span>
    )}
    {item.soon && (
      <span className="text-[9px] font-bold uppercase" style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-display)', letterSpacing: '0.14em' }}>
        Soon
      </span>
    )}
  </button>
);

const Sidebar: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.logout);
  const { signOut } = useClerk();
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('[Sidebar] signOut failed', err);
    } finally {
      clearAuth();
      history.replace('/login');
    }
  };
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace);
  const openQuickCapture = useUIStore((s) => s.openQuickCapture);
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const navigate = (path: string) => {
    history.push(path);
    if (window.matchMedia('(max-width: 768px)').matches) useUIStore.getState().setSidebarOpen(false);
  };

  const sections: NavSection[] = [
    {
      label: 'Workspace',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: <Icon path={<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>} /> },
        { path: '/onboard', label: 'New Thread', icon: <Icon path={<><path d="M12 5v14M5 12h14"/></>} /> },
      ],
    },
    {
      label: 'Knowledge',
      items: [
        { path: '/notes', label: 'Notes', icon: <Icon path={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>} /> },
        { path: '/thinking', label: 'Thinking', soon: true, icon: <Icon path={<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></>} /> },
        { path: '/graph', label: 'Graph', icon: <Icon path={<><circle cx="12" cy="12" r="3"/><circle cx="4" cy="6" r="2"/><circle cx="20" cy="6" r="2"/><circle cx="4" cy="18" r="2"/><circle cx="20" cy="18" r="2"/><line x1="12" y1="12" x2="4" y2="6"/><line x1="12" y1="12" x2="20" y2="6"/><line x1="12" y1="12" x2="4" y2="18"/><line x1="12" y1="12" x2="20" y2="18"/></>} /> },
      ],
    },
    {
      label: 'Decisions',
      items: [
        { path: '/decisions', label: 'Decision Journal', soon: true, icon: <Icon path={<><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>} /> },
        { path: '/reviews', label: 'Reviews', soon: true, icon: <Icon path={<><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></>} /> },
      ],
    },
    {
      label: 'People',
      items: [
        { path: '/relationships', label: 'Relationships', soon: true, icon: <Icon path={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>} /> },
      ],
    },
    {
      label: 'Projects',
      items: [
        { path: '/projects', label: 'Projects', soon: true, icon: <Icon path={<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></>} /> },
        { path: '/capabilities', label: 'Capabilities', icon: <Icon path={<><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></>} /> },
      ],
    },
  ];

  if (collapsed) {
    return (
      <div className="h-full flex flex-col items-center py-3 gap-0.5"
        style={{ width: '52px', background: 'var(--bg-deep)', borderRight: '1px solid var(--border)', flexShrink: 0 }}>
        <div className="w-8 h-8 flex items-center justify-center mb-2 flex-shrink-0"
          style={{ background: 'var(--crimson)', borderRadius: '6px', boxShadow: '0 0 10px rgba(192,57,43,0.4)' }}>
          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="2" fill="currentColor"/>
            <circle cx="5" cy="7" r="1.5" fill="currentColor" opacity="0.8"/>
            <circle cx="19" cy="7" r="1.5" fill="currentColor" opacity="0.8"/>
          </svg>
        </div>
        <button type="button" onClick={() => setCollapsed(false)}
          className="p-2 rounded hover:bg-white/5 transition-colors mb-1" style={{ color: 'var(--text-muted)' }}>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 17l5-5-5-5"/><path d="M6 17l5-5-5-5"/></svg>
        </button>
        <div className="flex-1 flex flex-col items-center gap-0.5 w-full px-1.5">
          {sections.flatMap(s => s.items).map(item => (
            <button key={item.path} type="button" onClick={item.soon ? undefined : () => navigate(item.path)}
              title={item.label} className="w-full flex items-center justify-center p-2 rounded transition-all"
              style={{ color: isActive(item.path) ? 'var(--crimson-light)' : 'var(--text-muted)', background: isActive(item.path) ? 'var(--crimson-dim)' : 'transparent', opacity: item.soon ? 0.35 : 1 }}>
              {item.icon}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col"
      style={{ width: '240px', background: 'var(--bg-deep)', borderRight: '1px solid var(--border)', flexShrink: 0 }}>

      <div className="flex items-center justify-between px-4 h-12 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--crimson)', borderRadius: '6px', boxShadow: '0 0 12px rgba(192,57,43,0.4)' }}>
            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="2" fill="currentColor"/>
              <circle cx="5" cy="7" r="1.5" fill="currentColor" opacity="0.8"/>
              <circle cx="19" cy="7" r="1.5" fill="currentColor" opacity="0.8"/>
              <circle cx="5" cy="17" r="1.5" fill="currentColor" opacity="0.55"/>
              <circle cx="19" cy="17" r="1.5" fill="currentColor" opacity="0.55"/>
            </svg>
          </div>
          <span className="font-black uppercase text-sm" style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', letterSpacing: '0.18em' }}>CapOS</span>
        </div>
        <button type="button" onClick={() => setCollapsed(true)}
          className="p-1.5 rounded transition-colors hover:bg-white/5" style={{ color: 'var(--text-muted)' }}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 17l-5-5 5-5"/><path d="M18 17l-5-5 5-5"/>
          </svg>
        </button>
      </div>

      {workspaces.length > 0 && (
        <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
          <select value={activeWorkspace?.id ?? ''} onChange={(e) => setActiveWorkspace(e.target.value)}
            className="w-full rounded px-2.5 py-1.5 text-xs outline-none"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.06em', background: 'var(--bg-mid)', border: '1px solid var(--border-mid)', color: 'var(--text-dim)' }}>
            {workspaces.map((ws) => <option key={ws.id} value={ws.id}>{ws.name}</option>)}
          </select>
        </div>
      )}

      <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
        <button type="button" onClick={() => openQuickCapture()}
          className="w-full flex items-center gap-2 px-3 py-2 rounded text-xs font-bold uppercase transition-all"
          style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.14em', background: 'var(--crimson)', color: '#fff', boxShadow: '0 0 14px rgba(192,57,43,0.35)' }}
          onMouseOver={e => { (e.currentTarget as HTMLElement).style.background = 'var(--crimson-light)'; }}
          onMouseOut={e => { (e.currentTarget as HTMLElement).style.background = 'var(--crimson)'; }}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          Quick Capture
          <kbd className="ml-auto text-[9px] opacity-60 font-mono">⌘N</kbd>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
        {sections.map((section) => (
          <div key={section.label}>
            <p className="px-3 mb-1 text-[9px] font-bold uppercase"
              style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-display)', letterSpacing: '0.22em' }}>
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink key={item.path} item={item} active={isActive(item.path)} onClick={() => navigate(item.path)} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-2 py-3 flex-shrink-0 space-y-1" style={{ borderTop: '1px solid var(--border)' }}>
        <NavLink
          item={{ path: '/settings', label: 'Settings', icon: <Icon path={<><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></>} /> }}
          active={isActive('/settings')} onClick={() => navigate('/settings')}
        />
        <div className="flex items-center gap-2 px-2 py-2 rounded mt-1"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--border)' }}>
          <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--crimson-border)' }}>
            <span className="text-[10px] font-bold tracking-wide"
              style={{ color: 'var(--crimson)', fontFamily: 'var(--font-display)', letterSpacing: '0.04em' }}>
              {(() => {
                const cleaned = user?.name?.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim() ?? '';
                if (!cleaned) return 'ME';
                const parts = cleaned.split(/\s+/);
                const initials = parts.length >= 2
                  ? `${parts[0][0]}${parts[parts.length - 1][0]}`
                  : cleaned.slice(0, 2);
                return initials.toUpperCase();
              })()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>
              {user?.name?.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim() || 'You'}
            </p>
            <p className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
          </div>
          <button type="button" onClick={handleSignOut} className="p-1.5 rounded transition-colors hover:bg-white/5 flex-shrink-0"
            style={{ color: 'var(--text-muted)' }} title="Sign out" aria-label="Sign out">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
